import type { MetricValues } from '../../PageConfigProvider/types/metric';
import type { TableData, TableDimension, TableDimensionValue } from '../types';

export const groupTableRows = (
  table: TableData,
  dimension: TableDimension
): { value: TableDimensionValue; rows: number[] }[] => {
  const column = table.dimensionColumns[dimension];
  if (!column) return [];

  const groups = new Map<
    number,
    { value: TableDimensionValue; rows: number[] }
  >();
  for (let row = 0; row < table.rowCount; row++) {
    const index = column.indices[row];
    const value = index === undefined ? undefined : column.values[index];
    if (index === undefined || value === undefined) continue;
    const group = groups.get(index);
    if (group) group.rows.push(row);
    else groups.set(index, { value, rows: [row] });
  }
  return Array.from(groups.values());
};

export const getTableValue = (
  table: TableData,
  metric: MetricValues,
  row: number
): number | null => {
  const value = table.metricColumns[metric]?.[row];
  return value === undefined || Number.isNaN(value) ? null : value;
};

export const getTableDimensionValue = (
  table: TableData,
  dimension: TableDimension,
  row: number
): TableDimensionValue | undefined => {
  const column = table.dimensionColumns[dimension];
  const index = column?.indices[row];
  return index === undefined ? undefined : column?.values[index];
};

export const getEntitySeries = (
  table: TableData,
  metric: MetricValues
): { value: TableDimensionValue; values: number[] }[] => {
  const [entity] = table.dimensions;
  if (entity === undefined) return [];
  const monthOf = (row: number) =>
    getTableDimensionValue(table, 'timeseries', row)?.id ?? '';
  return groupTableRows(table, entity).map(({ value, rows }) => ({
    value,
    values: [...rows]
      .sort((a, b) => monthOf(a).localeCompare(monthOf(b)))
      .flatMap((row) => getTableValue(table, metric, row) ?? []),
  }));
};

export const getEntityMonthlyValues = (
  table: TableData,
  metric: MetricValues
): { value: TableDimensionValue; byMonth: Map<string, number> }[] => {
  const [entity] = table.dimensions;
  if (entity === undefined) return [];
  return groupTableRows(table, entity).map(({ value, rows }) => ({
    value,
    byMonth: new Map(
      rows.flatMap((row) => {
        const month = getTableDimensionValue(table, 'timeseries', row)?.id;
        const value = getTableValue(table, metric, row);
        return month === undefined || value === null
          ? []
          : [[month, value] as const];
      })
    ),
  }));
};

export const selectTableMetrics = (
  table: TableData,
  metrics: MetricValues[]
): TableData => ({
  ...table,
  metrics,
  metricColumns: metrics.reduce<TableData['metricColumns']>(
    (columns, metric) => {
      columns[metric] = table.metricColumns[metric];
      return columns;
    },
    {}
  ),
});

export const selectEntityRows = (
  table: TableData,
  entityId: string
): TableData => {
  const [entity] = table.dimensions;
  if (entity === undefined) return table;
  const rows: number[] = [];
  for (let row = 0; row < table.rowCount; row++) {
    if (getTableDimensionValue(table, entity, row)?.id === entityId) {
      rows.push(row);
    }
  }
  if (rows.length === table.rowCount) return table;

  const dimensionColumns: TableData['dimensionColumns'] = {};
  for (const dimension of table.dimensions) {
    const column = table.dimensionColumns[dimension];
    if (column) {
      dimensionColumns[dimension] = {
        values: column.values,
        indices: Uint32Array.from(rows, (row) => column.indices[row] ?? 0),
      };
    }
  }
  const metricColumns: TableData['metricColumns'] = {};
  for (const metric of table.metrics) {
    const column = table.metricColumns[metric];
    if (column) {
      metricColumns[metric] = Float64Array.from(
        rows,
        (row) => column[row] ?? Number.NaN
      );
    }
  }
  return { ...table, rowCount: rows.length, dimensionColumns, metricColumns };
};

export const convertTableCurrency = (
  table: TableData,
  exchangeRate: number
): TableData => {
  const salary = table.metricColumns.salary;
  if (!salary || exchangeRate === 1) return table;
  return {
    ...table,
    metricColumns: {
      ...table.metricColumns,
      salary: salary.map((value) => value * exchangeRate),
    },
  };
};
